import { z } from 'zod';

import { PageContentSchema } from './page-content';
import {
  StepListenerSchema,
  type StepListener,
} from './structure';
import {
  getFunnelEventSemantic,
  type FunnelEventSemanticDefinition,
} from './semantic-events';
import {
  normalizeEventName,
  type UIEventBinding,
  type UITrigger,
} from './ui-events';

export const SemanticBehaviorStepSchema = z
  .object({
    surface: z.literal('funnel'),
    pageContent: PageContentSchema,
    listeners: z.array(StepListenerSchema),
  })
  .strict();

export type SemanticBehaviorStep = z.infer<
  typeof SemanticBehaviorStepSchema
>;

export const SemanticBehaviorIssueCodeSchema = z.enum([
  'schema_invalid',
  'unsupported_trigger',
  'missing_reaction',
  'duplicate_reaction',
  'unexpected_action',
  'authoritative_binding',
  'missing_parameter',
  'managed_identity_incomplete',
  'managed_identity_mismatch',
  'duplicate_managed_binding',
  'ambiguous_control',
  'missing_source_correlation',
  'source_correlation_mismatch',
]);
export type SemanticBehaviorIssueCode = z.infer<
  typeof SemanticBehaviorIssueCodeSchema
>;

export interface SemanticBehaviorIssue {
  code: SemanticBehaviorIssueCode;
  path: string;
  message: string;
}

export interface SemanticBehaviorValidationResult {
  valid: boolean;
  issues: SemanticBehaviorIssue[];
  data: SemanticBehaviorStep | null;
}

interface IndexedBinding {
  blockIndex: number;
  blockId: string;
  bindingIndex: number;
  trigger: UITrigger;
  binding: UIEventBinding;
}

const parameterAliases: Record<string, readonly string[]> = {
  productId: ['productId', 'product_id', 'sku'],
  variantId: ['variantId', 'variant_id'],
  couponCode: ['couponCode', 'coupon_code'],
  lineId: ['lineId', 'line_id'],
};

function collectBindings(step: SemanticBehaviorStep): IndexedBinding[] {
  const bindings: IndexedBinding[] = [];

  step.pageContent.blocks.forEach((block, blockIndex) => {
    if (block.events === undefined) {
      return;
    }
    Object.entries(block.events).forEach(([trigger, triggerBindings]) => {
      triggerBindings.forEach((binding, bindingIndex) => {
        bindings.push({
          blockIndex,
          blockId: block.id,
          bindingIndex,
          trigger: trigger as UITrigger,
          binding,
        });
      });
    });
  });

  return bindings;
}

function bindingPath(indexed: IndexedBinding): string {
  return `pageContent.blocks.${indexed.blockIndex}.events.${indexed.trigger}.${indexed.bindingIndex}`;
}

function listenerPath(index: number): string {
  return `listeners.${index}`;
}

function bindingParameterKeys(indexed: IndexedBinding): Set<string> {
  return new Set([
    ...Object.keys(indexed.binding.params ?? {}),
    ...Object.keys(indexed.binding.paramsFrom ?? {}),
  ]);
}

function managedBehaviorKey(
  identity: UIEventBinding['managedBehavior'],
): string | null {
  return identity === undefined
    ? null
    : `${identity.owner}:${identity.behaviorSetId}:${identity.revision}`;
}

function commandListenerCandidates(
  listeners: readonly StepListener[],
  eventName: string,
  actionType: string,
): Array<{ listener: StepListener; index: number }> {
  return listeners
    .map((listener, index) => ({ listener, index }))
    .filter(
      ({ listener }) =>
        normalizeEventName(listener.eventName) === eventName &&
        listener.config.reaction?.kind === 'run_action' &&
        listener.config.reaction.action?.type === actionType,
    );
}

function matchingCommandListeners(
  listeners: readonly StepListener[],
  indexed: IndexedBinding,
  actionType: string,
): Array<{ listener: StepListener; index: number }> {
  const candidates = commandListenerCandidates(
    listeners,
    normalizeEventName(indexed.binding.name),
    actionType,
  );
  const bindingIdentityKey = managedBehaviorKey(
    indexed.binding.managedBehavior,
  );

  if (bindingIdentityKey === null) {
    const authoredCandidates = candidates.filter(({ listener }) => {
      const action =
        listener.config.reaction?.kind === 'run_action'
          ? listener.config.reaction.action
          : undefined;
      return (
        listener.managedBehavior === undefined &&
        action?.managedBehavior === undefined
      );
    });
    const sourceMatches = authoredCandidates.filter(
      ({ listener }) =>
        listener.config.sourceBlockId === indexed.blockId &&
        listener.config.sourceTrigger === indexed.trigger,
    );
    return sourceMatches.length > 0 ? sourceMatches : authoredCandidates;
  }

  const exactMatches = candidates.filter(({ listener }) => {
    const action =
      listener.config.reaction?.kind === 'run_action'
        ? listener.config.reaction.action
        : undefined;
    return (
      managedBehaviorKey(listener.managedBehavior) === bindingIdentityKey &&
      managedBehaviorKey(action?.managedBehavior) === bindingIdentityKey
    );
  });

  // Preserve the precise incomplete/mismatched identity diagnostics for a
  // single legacy bundle. With multiple compatible listeners, never guess
  // which control owns a reaction.
  return exactMatches.length > 0
    ? exactMatches
    : candidates.length === 1
      ? candidates
      : [];
}

function availableParameterKeys(
  indexed: IndexedBinding,
  listener: StepListener,
): Set<string> {
  const keys = bindingParameterKeys(indexed);
  const reaction = listener.config.reaction;
  if (reaction?.kind === 'run_action' && reaction.action !== undefined) {
    const config = reaction.action.config;
    if (
      config !== undefined &&
      config !== null &&
      typeof config === 'object' &&
      !Array.isArray(config)
    ) {
      Object.keys(config).forEach((key) => keys.add(key));
    }
  }
  return keys;
}

function validateRequiredParameters(
  indexed: IndexedBinding,
  semantic: FunnelEventSemanticDefinition,
  keys: ReadonlySet<string>,
  issues: SemanticBehaviorIssue[],
): void {
  semantic.requiredParameters.forEach((required) => {
    if (
      semantic.eventName === 'lead_captured' &&
      required === 'value' &&
      indexed.binding.paramsFrom?.value !== '$value'
    ) {
      issues.push({
        code: 'missing_parameter',
        path: bindingPath(indexed),
        message:
          'lead_captured must map paramsFrom.value to $value so the submitted value stays local to the capture capability.',
      });
      return;
    }
    if (!hasParameter(keys, required)) {
      issues.push({
        code: 'missing_parameter',
        path: bindingPath(indexed),
        message: `${semantic.eventName} is missing required parameter ${required}.`,
      });
    }
  });
}

function hasParameter(keys: ReadonlySet<string>, required: string): boolean {
  return (parameterAliases[required] ?? [required]).some((key) =>
    keys.has(key),
  );
}

function validateManagedIdentity(
  indexed: IndexedBinding,
  listener: StepListener,
  listenerIndex: number,
  issues: SemanticBehaviorIssue[],
): void {
  const action =
    listener.config.reaction?.kind === 'run_action'
      ? listener.config.reaction.action
      : undefined;
  const identities = [
    indexed.binding.managedBehavior,
    listener.managedBehavior,
    action?.managedBehavior,
  ];
  if (identities.every((identity) => identity === undefined)) {
    return;
  }
  if (identities.some((identity) => identity === undefined)) {
    issues.push({
      code: 'managed_identity_incomplete',
      path: bindingPath(indexed),
      message:
        'A managed semantic behavior requires the same identity on its binding, listener, and action.',
    });
    return;
  }

  const identityKeys = new Set(
    identities.map((identity) =>
      identity === undefined
        ? ''
        : `${identity.owner}:${identity.behaviorSetId}:${identity.revision}`,
    ),
  );
  if (identityKeys.size !== 1) {
    issues.push({
      code: 'managed_identity_mismatch',
      path: `${bindingPath(indexed)} <-> ${listenerPath(listenerIndex)}`,
      message:
        'Managed binding, listener, and action identities must match exactly.',
    });
  }
}

function validateSourceCorrelation(
  indexed: IndexedBinding,
  semantic: FunnelEventSemanticDefinition,
  listener: StepListener,
  listenerIndex: number,
  issues: SemanticBehaviorIssue[],
): void {
  if (semantic.actionType === null) {
    return;
  }
  const { sourceBlockId, sourceTrigger } = listener.config;

  if (
    (sourceBlockId !== undefined && sourceBlockId !== indexed.blockId) ||
    (sourceTrigger !== undefined && sourceTrigger !== indexed.trigger)
  ) {
    issues.push({
      code: 'source_correlation_mismatch',
      path: `${bindingPath(indexed)} <-> ${listenerPath(listenerIndex)}`,
      message:
        'Listener sourceBlockId and sourceTrigger must match the UI control that owns the managed binding.',
    });
  }
}

function validateRepeatedCommandSourceCorrelation(
  bindings: readonly IndexedBinding[],
  listeners: readonly StepListener[],
  issues: SemanticBehaviorIssue[],
): void {
  listeners.forEach((listener, listenerIndex) => {
    const reaction = listener.config.reaction;
    if (reaction?.kind !== 'run_action' || reaction.action === undefined) {
      return;
    }
    const eventName = normalizeEventName(listener.eventName);
    const actionType = reaction.action.type;
    const compatibleListeners = commandListenerCandidates(
      listeners,
      eventName,
      actionType,
    );
    const compatibleBindings = bindings.filter((candidate) => {
      const candidateSemantic = getFunnelEventSemantic(
        normalizeEventName(candidate.binding.name),
      );
      return (
        candidateSemantic?.eventName === eventName &&
        candidateSemantic.actionType === actionType
      );
    });
    if (compatibleBindings.length === 0) {
      return;
    }
    if (
      compatibleListeners.length <= 1 &&
      compatibleBindings.length <= 1
    ) {
      return;
    }

    const { sourceBlockId, sourceTrigger } = listener.config;
    if (sourceBlockId === undefined || sourceTrigger === undefined) {
      issues.push({
        code: 'missing_source_correlation',
        path: listenerPath(listenerIndex),
        message:
          'Repeated command listeners and controls require sourceBlockId and sourceTrigger so each UI control executes only its own reaction.',
      });
      return;
    }
    if (
      !compatibleBindings.some(
        (candidate) =>
          candidate.blockId === sourceBlockId &&
          candidate.trigger === sourceTrigger,
      )
    ) {
      issues.push({
        code: 'source_correlation_mismatch',
        path: listenerPath(listenerIndex),
        message:
          'Listener sourceBlockId and sourceTrigger do not match a compatible UI control.',
      });
    }
  });
}

function validateCommandBinding(
  indexed: IndexedBinding,
  semantic: FunnelEventSemanticDefinition,
  listeners: readonly StepListener[],
  issues: SemanticBehaviorIssue[],
): void {
  if (semantic.actionType === null) {
    return;
  }

  const matches = matchingCommandListeners(
    listeners,
    indexed,
    semantic.actionType,
  );
  if (matches.length === 0) {
    issues.push({
      code: 'missing_reaction',
      path: bindingPath(indexed),
      message: `${semantic.eventName} requires exactly one ${semantic.actionType} reaction.`,
    });
    return;
  }
  if (matches.length > 1) {
    issues.push({
      code: 'duplicate_reaction',
      path: bindingPath(indexed),
      message: `${semantic.eventName} resolves to more than one ${semantic.actionType} reaction.`,
    });
    return;
  }

  const match = matches[0];
  if (match === undefined) {
    return;
  }
  validateRequiredParameters(
    indexed,
    semantic,
    availableParameterKeys(indexed, match.listener),
    issues,
  );
  validateManagedIdentity(indexed, match.listener, match.index, issues);
  validateSourceCorrelation(
    indexed,
    semantic,
    match.listener,
    match.index,
    issues,
  );
}

function validateHandlerFreeSemantic(
  indexed: IndexedBinding,
  semantic: FunnelEventSemanticDefinition,
  listeners: readonly StepListener[],
  issues: SemanticBehaviorIssue[],
): void {
  if (semantic.actionType !== null) {
    return;
  }
  validateRequiredParameters(
    indexed,
    semantic,
    bindingParameterKeys(indexed),
    issues,
  );
  listeners.forEach((listener, listenerIndex) => {
    if (
      normalizeEventName(listener.eventName) === semantic.eventName &&
      listener.config.reaction?.kind === 'run_action'
    ) {
      issues.push({
        code: 'unexpected_action',
        path: listenerPath(listenerIndex),
        message: `${semantic.eventName} is ${semantic.kind} and cannot execute a Storefront action.`,
      });
    }
  });
}

function validateManagedBindingUniqueness(
  bindings: readonly IndexedBinding[],
  issues: SemanticBehaviorIssue[],
): void {
  const firstPathByIdentity = new Map<string, string>();
  bindings.forEach((indexed) => {
    const identity = indexed.binding.managedBehavior;
    if (identity === undefined) {
      return;
    }
    const key = `${identity.owner}:${identity.behaviorSetId}:${identity.revision}`;
    const currentPath = bindingPath(indexed);
    const firstPath = firstPathByIdentity.get(key);
    if (firstPath !== undefined) {
      issues.push({
        code: 'duplicate_managed_binding',
        path: currentPath,
        message: `Managed behavior identity is already used by ${firstPath}.`,
      });
    } else {
      firstPathByIdentity.set(key, currentPath);
    }
  });
}

function validateExecutableBindingUniqueness(
  bindings: readonly IndexedBinding[],
  issues: SemanticBehaviorIssue[],
): void {
  const firstPathByControl = new Map<string, string>();
  bindings.forEach((indexed) => {
    const semantic = getFunnelEventSemantic(
      normalizeEventName(indexed.binding.name),
    );
    if (semantic?.actionType === null || semantic === undefined) {
      return;
    }
    const key = `${indexed.blockIndex}:${indexed.trigger}`;
    const currentPath = bindingPath(indexed);
    const firstPath = firstPathByControl.get(key);
    if (firstPath !== undefined) {
      issues.push({
        code: 'ambiguous_control',
        path: currentPath,
        message: `One UI control cannot execute more than one semantic command; ${firstPath} already owns this trigger.`,
      });
    } else {
      firstPathByControl.set(key, currentPath);
    }
  });
}

const MULTI_CONTROL_CTA_TYPES = new Set([
  'cta',
  'cta-button',
  'cta_button',
  'cta-button-group',
  'cta_button_group',
]);

const LOCAL_CTA_TARGETS = new Set([
  '#accept',
  '#add-to-cart',
  '#decline',
  '#dismiss',
]);

function validateCtaCommandOwnership(
  indexed: IndexedBinding,
  semantic: FunnelEventSemanticDefinition,
  step: SemanticBehaviorStep,
  issues: SemanticBehaviorIssue[],
): void {
  if (
    semantic.actionType === null ||
    indexed.trigger !== 'onClick'
  ) {
    return;
  }
  const block = step.pageContent.blocks[indexed.blockIndex];
  if (block === undefined || !MULTI_CONTROL_CTA_TYPES.has(block.type)) {
    return;
  }
  const buttons = block.props?.buttons;
  if (!Array.isArray(buttons)) {
    return;
  }
  const primary = buttons[0];
  if (
    primary === null ||
    typeof primary !== 'object' ||
    Array.isArray(primary)
  ) {
    return;
  }
  const url = (primary as Record<string, unknown>).url;
  if (
    typeof url === 'string' &&
    url.trim() !== '' &&
    !LOCAL_CTA_TARGETS.has(url.trim())
  ) {
    issues.push({
      code: 'ambiguous_control',
      path: `pageContent.blocks.${indexed.blockIndex}.props.buttons.0.url`,
      message:
        'A primary CTA cannot combine an executable semantic command with a navigation URL.',
    });
  }
}

export function validateSemanticBehaviorStep(
  input: unknown,
): SemanticBehaviorValidationResult {
  const parsed = SemanticBehaviorStepSchema.safeParse(input);
  if (!parsed.success) {
    return {
      valid: false,
      data: null,
      issues: parsed.error.issues.map((issue) => ({
        code: 'schema_invalid',
        path: issue.path.join('.'),
        message: issue.message,
      })),
    };
  }

  const issues: SemanticBehaviorIssue[] = [];
  const bindings = collectBindings(parsed.data);
  validateManagedBindingUniqueness(bindings, issues);
  validateExecutableBindingUniqueness(bindings, issues);
  validateRepeatedCommandSourceCorrelation(
    bindings,
    parsed.data.listeners,
    issues,
  );

  bindings.forEach((indexed) => {
    const semantic = getFunnelEventSemantic(indexed.binding.name);
    if (semantic === undefined) {
      return;
    }
    if (semantic.kind === 'authoritative_result') {
      issues.push({
        code: 'authoritative_binding',
        path: bindingPath(indexed),
        message: `${semantic.eventName} must be emitted by ${semantic.successBoundaryOwner} and cannot be authored as a browser block binding.`,
      });
      return;
    }
    if (!semantic.acceptedTriggers.includes(indexed.trigger)) {
      issues.push({
        code: 'unsupported_trigger',
        path: bindingPath(indexed),
        message: `${semantic.eventName} does not support trigger ${indexed.trigger}.`,
      });
    }
    validateCtaCommandOwnership(indexed, semantic, parsed.data, issues);
    validateCommandBinding(
      indexed,
      semantic,
      parsed.data.listeners,
      issues,
    );
    validateHandlerFreeSemantic(
      indexed,
      semantic,
      parsed.data.listeners,
      issues,
    );
  });

  return {
    valid: issues.length === 0,
    issues,
    data: parsed.data,
  };
}
